import React, { useState, useRef } from 'react';
import { Download, Upload, FileJson, CheckCircle, AlertCircle, Loader2, Database, ArrowRight } from 'lucide-react';
import axios from 'axios';

const DataMigration = () => {
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Export all data (plans + tenants)
  const handleExportAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/data-migration/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cloudcampus_migration_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      alert('Data exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Export only subscription plans
  const handleExportPlans = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/data-migration/export/plans-only`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `subscription_plans_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      alert('Subscription Plans exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Export only tenants
  const handleExportTenants = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/data-migration/export/tenants-only`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tenants_data_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      alert('Tenants data exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
      setImportError(null);
      
      // Read and preview the file
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target.result);
          setPreviewData(data);
        } catch (err) {
          setImportError('Invalid JSON file');
          setPreviewData(null);
        }
      };
      reader.readAsText(file);
    }
  };

  // Handle import
  const handleImport = async () => {
    if (!previewData) {
      alert('Please select a JSON file first');
      return;
    }
    
    setLoading(true);
    setImportResult(null);
    setImportError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/data-migration/import`, previewData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setImportResult(response.data);
      setSelectedFile(null);
      setPreviewData(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Import error:', error);
      setImportError(error.response?.data?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Database className="h-7 w-7 text-blue-600" />
          Data Migration
        </h1>
        <p className="text-gray-600 mt-1">
          Migrate Subscription Plans and Tenants data from Preview to Live site
        </p>
      </div>

      {/* Instructions Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-800 mb-2">📋 How to use:</h3>
        <div className="flex items-center gap-4 text-sm text-blue-700">
          <div className="flex items-center gap-2">
            <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">1</span>
            <span>Download from Preview site</span>
          </div>
          <ArrowRight className="h-4 w-4" />
          <div className="flex items-center gap-2">
            <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">2</span>
            <span>Login to Live site</span>
          </div>
          <ArrowRight className="h-4 w-4" />
          <div className="flex items-center gap-2">
            <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">3</span>
            <span>Upload JSON file</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Export Section */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <Download className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Export Data</h2>
              <p className="text-sm text-gray-500">Download data in JSON format</p>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleExportAll}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
              Download All (Plans + Tenants)
            </button>
            
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleExportPlans}
                disabled={loading}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 text-sm"
              >
                <FileJson className="h-4 w-4" />
                Plans Only
              </button>
              <button
                onClick={handleExportTenants}
                disabled={loading}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 text-sm"
              >
                <FileJson className="h-4 w-4" />
                Tenants Only
              </button>
            </div>
          </div>
        </div>

        {/* Import Section */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Upload className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Import Data</h2>
              <p className="text-sm text-gray-500">Upload JSON file to import data</p>
            </div>
          </div>

          <div className="space-y-4">
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="hidden"
              />
              <FileJson className="h-10 w-10 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                {selectedFile ? selectedFile.name : 'Click to select JSON file'}
              </p>
            </div>

            {/* Preview */}
            {previewData && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-700 mb-2">Preview:</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>📅 Exported: {previewData.export_info?.exported_at?.slice(0, 10) || 'N/A'}</p>
                  <p>📦 Subscription Plans: {previewData.subscription_plans?.length || 0}</p>
                  <p>🏢 Tenants: {previewData.tenants?.length || 0}</p>
                </div>
              </div>
            )}

            <button
              onClick={handleImport}
              disabled={loading || !previewData}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
              Import Data
            </button>
          </div>
        </div>
      </div>

      {/* Import Result */}
      {importResult && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="h-6 w-6 text-green-600" />
            <h3 className="font-semibold text-green-800">Import Successful!</h3>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-white rounded-lg p-3">
              <h4 className="font-medium text-gray-700">Subscription Plans:</h4>
              <p className="text-green-600">✓ Created: {importResult.results?.subscription_plans?.created || 0}</p>
              <p className="text-blue-600">↻ Updated: {importResult.results?.subscription_plans?.updated || 0}</p>
              <p className="text-gray-500">○ Skipped: {importResult.results?.subscription_plans?.skipped || 0}</p>
            </div>
            <div className="bg-white rounded-lg p-3">
              <h4 className="font-medium text-gray-700">Tenants:</h4>
              <p className="text-green-600">✓ Created: {importResult.results?.tenants?.created || 0}</p>
              <p className="text-blue-600">↻ Updated: {importResult.results?.tenants?.updated || 0}</p>
              <p className="text-gray-500">○ Skipped: {importResult.results?.tenants?.skipped || 0}</p>
            </div>
          </div>
          {importResult.results?.errors?.length > 0 && (
            <div className="mt-4 bg-yellow-50 rounded-lg p-3">
              <h4 className="font-medium text-yellow-800 mb-2">⚠️ Warnings:</h4>
              <ul className="text-sm text-yellow-700 list-disc list-inside">
                {importResult.results.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Import Error */}
      {importError && (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-800">{importError}</p>
          </div>
        </div>
      )}

      {/* Note */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="font-medium text-yellow-800 mb-2">⚠️ Important Notes:</h4>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• Import will update existing Subscription Plans and Tenants module settings</li>
          <li>• New tenants will NOT be created - only existing tenant settings will be updated</li>
          <li>• If a tenant doesn't exist on Live site, create it first before importing</li>
        </ul>
      </div>
    </div>
  );
};

export default DataMigration;
